// MIDAS V3 — Chat message list (Phase 2 + Phase 6)
//
// Scroll container — max-width 820, centered. Renders chronological mix of:
//   • <ChatUserMessageV3> for `role === "user"`
//   • <ChatBotMessageV3> for `role === "assistant"`
//   • Day separators between days (v3 re-skinned eyebrow, NOT the v2 MUI
//     ChatDaySeparator)
//
// Auto-scroll-to-bottom on append (mirrors v2 behaviour).
//
// Streaming state: when the chatBot slice reports an in-flight stream for
// the active thread, we append a <StreamingStateV3 /> chrome at the bottom
// of the list (after the last persisted message). Phase 6 replaces the
// Phase 2 minimal placeholder with the full chrome — animated logo + halo +
// step chip + shimmer + elapsed timer.
//
// We feed StreamingStateV3 the buffer's `currentStep` directly so when the
// bot starts emitting step keys it lights up without any further frontend
// change.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChatMessage, POPUP_THREAD_ID } from "../../../../shared/chatBot/models/chatBotModels";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import ChatBotMessageV3 from "./ChatBotMessageV3";
import ChatUserMessageV3 from "./ChatUserMessageV3";
import ChatWelcomeV3 from "./ChatWelcomeV3";
import StreamingMarkdownV3 from "./StreamingMarkdownV3";
import StreamingStateV3 from "./StreamingStateV3";

// ═══════════════════════════════════════════════════════════════
//  Helpers — verbatim from V2 logic
// ═══════════════════════════════════════════════════════════════

const isDifferentDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() !== db.getFullYear() ||
    da.getMonth() !== db.getMonth() ||
    da.getDate() !== db.getDate()
  );
};

const isNewGroup = (a: ChatMessage, b: ChatMessage): boolean => {
  const diff = Math.abs(
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return diff > 5 * 60 * 1000;
};

// ═══════════════════════════════════════════════════════════════
//  Day separator — v3-themed eyebrow
// ═══════════════════════════════════════════════════════════════

interface DaySeparatorProps {
  date: string;
}

const DaySeparatorV3: React.FC<DaySeparatorProps> = ({ date }) => {
  const { translate } = useTranslation("chatBot");

  const label = useMemo(() => {
    const msgDate = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(msgDate, today)) {
      return translate("today", { defaultValue: "Today" }) as string;
    }
    if (sameDay(msgDate, yesterday)) {
      return translate("yesterday", { defaultValue: "Yesterday" }) as string;
    }
    return msgDate.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [date, translate]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "12px 0 14px 0",
        gap: 10,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: 1,
          height: 1,
          background: "var(--border-soft)",
          maxWidth: 120,
        }}
      />
      <span
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.1em",
          color: "var(--faint)",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        — {label} —
      </span>
      <span
        aria-hidden="true"
        style={{
          flex: 1,
          height: 1,
          background: "var(--border-soft)",
          maxWidth: 120,
        }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ChatMessageListV3
// ═══════════════════════════════════════════════════════════════

export interface ChatMessageListV3Props {
  /** Thread id for buffer-scoped streaming reads. Mirrors v2 MIDAS-SCOPESTREAM. */
  threadId?: string;
}

const ChatMessageListV3: React.FC<ChatMessageListV3Props> = ({ threadId }) => {
  const messages = useAppSelector((state) => state.chatBot.messages);

  // Thread-buffer scoped streaming state (verbatim selector pattern from v2).
  const effectiveThreadId = threadId || POPUP_THREAD_ID;
  const threadBuffer = useAppSelector(
    (state) => state.chatBot.threadBuffers[effectiveThreadId]
  );
  const globalIsLoading = useAppSelector((state) => state.chatBot.isLoading);
  const globalStreamingText = useAppSelector(
    (state) => state.chatBot.streamingText
  );
  const isLoading = threadId
    ? threadBuffer?.isLoading || false
    : globalIsLoading;
  // Step key (e.g. "analyzing") from the buffer. Will be null today —
  // Q-MIDAS-V3-4 tracks the bot-side emit. StreamingStateV3 falls back to
  // elapsed-time buckets when this is null/undefined.
  const currentStep = threadBuffer?.currentStep ?? null;
  // Progressive token stream — populated by chatBotSlice.appendStreamingToken
  // per SSE chunk. V3 previously did NOT subscribe to this (the bug), so
  // users only saw the chrome until the message finalised. Mirrors the V2
  // selector pattern at ChatMessageListV2.tsx:247-253: thread-buffer scoped
  // when a threadId is supplied, falls back to the global field otherwise.
  const streamingText = threadId
    ? threadBuffer?.streamingText || ""
    : globalStreamingText || "";

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const prevMessageCount = useRef(messages.length);

  // Track streaming start so the streaming chrome can show elapsed seconds.
  // We anchor at the first frame the buffer reports isLoading=true and reset
  // when the stream ends. This survives child re-renders.
  const streamingStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading) {
      if (streamingStartedAtRef.current === null) {
        streamingStartedAtRef.current = Date.now();
      }
    } else {
      streamingStartedAtRef.current = null;
    }
  }, [isLoading]);
  const startedAt = streamingStartedAtRef.current ?? Date.now();

  const handleScroll = () => {
    if (!listRef.current) return;
    const el = listRef.current;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distFromBottom > 100);
  };

  // Auto-scroll on new message append (unless the user has scrolled up).
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      if (!userScrolledUp) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, userScrolledUp]);

  // Auto-scroll while streaming (the bot output grows; the user expects to
  // follow it). Don't fight the user if they scrolled up. `streamingText`
  // is in the dep array so the viewport pins to the bottom as prose grows
  // — mirrors V2 at ChatMessageListV2.tsx:327-331.
  useEffect(() => {
    if (isLoading && !userScrolledUp) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isLoading, userScrolledUp, streamingText]);

  // Empty thread → welcome state.
  if (messages.length === 0 && !isLoading) {
    return <ChatWelcomeV3 />;
  }

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "26px 32px",
        scrollBehavior: "smooth",
      }}
    >
      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
        }}
      >
        {messages.map((msg, index) => {
          const prev = index > 0 ? messages[index - 1] : null;
          const next = index < messages.length - 1 ? messages[index + 1] : null;

          const showDaySeparator =
            !prev || isDifferentDay(prev.timestamp, msg.timestamp);
          const isFirstInGroup =
            !prev || prev.role !== msg.role || isNewGroup(prev, msg);

          // For phase 2 we don't render "showTimestamp at end of group" for
          // user bubbles unless it's the very last in a thread — keeps the
          // chat clean. v2 uses isLastInGroup; we keep the same signal.
          const isLastInGroup =
            !next || next.role !== msg.role || isNewGroup(msg, next);

          return (
            <React.Fragment key={msg.id}>
              {showDaySeparator && <DaySeparatorV3 date={msg.timestamp} />}
              {msg.role === "assistant" ? (
                <ChatBotMessageV3 message={msg} showAvatar={isFirstInGroup} />
              ) : (
                <ChatUserMessageV3
                  message={msg}
                  showTimestamp={isLastInGroup}
                />
              )}
            </React.Fragment>
          );
        })}
        {/* Streaming handoff — mirrors V2 at ChatMessageListV2.tsx:399-421.
            Two mutually exclusive branches:
              • isLoading && !streamingText → chrome (animated hairline +
                step pill + elapsed timer). Shows ONLY before the first
                token, so the user gets an immediate "thinking" signal.
              • isLoading && streamingText  → progressive prose grows in
                place of the chrome. The timer and step pill disappear
                because time-to-first-token is the only metric that
                matters once tokens are arriving (TTFT-priority principle).
            Final-state ChatBotMessageV3 takes over when the thunk lands
            the persisted message and isLoading flips back to false. */}
        {isLoading && !streamingText && (
          <StreamingStateV3
            startedAt={startedAt}
            currentStep={currentStep}
          />
        )}
        {isLoading && streamingText && (
          <StreamingMarkdownV3
            text={streamingText}
            startedAt={startedAt}
          />
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default ChatMessageListV3;
